// src/components/NadscriptionExport.js
import React, { useState, useEffect } from 'react';

const NADSCRIPTIONS_API = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/nadscriptions`
  : 'https://api.doctordripp.com/api/nadscriptions';

function NadscriptionExport({ jwtToken }) {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('nadscriptions-genesis');
  const [stats, setStats] = useState({
    totalSupply: '-',
    uniqueHolders: '-',
    rarestSkull: '-',
    skullDistribution: []
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadCampaigns();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      refreshStats();
    }
  }, [selectedCampaign]);

  const loadCampaigns = async () => {
    try {
      const response = await fetch(`${NADSCRIPTIONS_API}?action=list-campaigns`);
      const data = await response.json();
      if (data.success && data.campaigns) {
        setCampaigns(data.campaigns);
        if (data.campaigns.length > 0 && !selectedCampaign) {
          setSelectedCampaign(data.campaigns[0].campaign_name);
        }
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const refreshStats = async () => {
    try {
      const response = await fetch(`${NADSCRIPTIONS_API}?action=collection-stats&campaignName=${selectedCampaign}`);
      const data = await response.json();
      
      if (data.success && data.stats) {
        const rarest = data.stats.skullDistribution.length > 0 
          ? data.stats.skullDistribution.sort((a, b) => a.count - b.count)[0] 
          : null;
        setStats({
          totalSupply: data.stats.totalSupply,
          uniqueHolders: data.stats.uniqueHolders,
          rarestSkull: rarest ? rarest.skull : '-',
          skullDistribution: data.stats.skullDistribution
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setMessage('⚠️ Error loading stats');
    }
  };

  const exportCollection = async (format) => {
    setLoading(true);
    setMessage(`Exporting collection as ${format.toUpperCase()}...`);
    
    try {
      const url = `${NADSCRIPTIONS_API}?action=export-collection&campaignName=${selectedCampaign}&format=${format}`;
      
      if (format === 'csv') {
        window.open(url, '_blank');
        setMessage('✅ CSV download started!');
      } else {
        const response = await fetch(url);
        const data = await response.json();
        
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${selectedCampaign}-${format}.json`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
        
        setMessage(`✅ Downloaded ${data.totalSupply || data.mints?.length || 0} items!`);
      }
    } catch (error) {
      console.error('Error exporting collection:', error);
      setMessage('❌ Export failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h3 style={{ color: '#06d6a0', marginBottom: '1.5rem' }}>📦 Collection Export & Analytics</h3>
      
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ marginRight: '1rem', fontWeight: 'bold' }}>Select Campaign:</label>
        <select 
          value={selectedCampaign} 
          onChange={(e) => setSelectedCampaign(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            border: '1px solid #555',
            background: '#333',
            color: 'white',
            fontSize: '1rem'
          }}
        >
          {campaigns.map(campaign => (
            <option key={campaign.campaign_name} value={campaign.campaign_name}>
              {campaign.campaign_name} ({campaign.current_mint_number - 1} minted)
            </option>
          ))}
        </select>
        <button
          onClick={refreshStats}
          style={{
            marginLeft: '1rem',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            border: 'none',
            background: '#555',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          🔄 Refresh Stats
        </button>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'rgba(6, 214, 160, 0.1)',
          padding: '1.5rem',
          borderRadius: '12px',
          textAlign: 'center',
          border: '1px solid rgba(6, 214, 160, 0.3)'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#06d6a0' }}>
            {stats.totalSupply}
          </div>
          <div style={{ color: '#aaa', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            Total Supply
          </div>
        </div>

        <div style={{
          background: 'rgba(0, 245, 255, 0.1)',
          padding: '1.5rem',
          borderRadius: '12px',
          textAlign: 'center',
          border: '1px solid rgba(0, 245, 255, 0.3)'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#00f5ff' }}>
            {stats.uniqueHolders}
          </div>
          <div style={{ color: '#aaa', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            Unique Holders
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 215, 0, 0.1)',
          padding: '1.5rem',
          borderRadius: '12px',
          textAlign: 'center',
          border: '1px solid rgba(255, 215, 0, 0.3)'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#ffd700' }}>
            {stats.rarestSkull}
          </div>
          <div style={{ color: '#aaa', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            Rarest Skull
          </div>
        </div>
      </div>

      {/* Skull Distribution */}
      {stats.skullDistribution.length > 0 && (
        <div style={{
          background: 'rgba(0,0,0,0.2)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h4 style={{ color: 'white', marginBottom: '1rem' }}>💀 Skull Distribution</h4>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {stats.skullDistribution.map((s, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px'
                }}
              >
                <span style={{ color: '#aaa' }}>{s.skull}</span>
                <span style={{ color: 'white', fontWeight: 'bold' }}>
                  {s.count} ({s.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Export Buttons */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
        marginBottom: '1rem'
      }}>
        <button
          onClick={() => exportCollection('json')}
          disabled={loading}
          style={{
            flex: 1,
            minWidth: '150px',
            padding: '1rem',
            borderRadius: '12px',
            border: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            fontSize: '1rem',
            opacity: loading ? 0.6 : 1
          }}
        >
          📄 Export JSON
        </button>

        <button
          onClick={() => exportCollection('metadata')}
          disabled={loading}
          style={{
            flex: 1,
            minWidth: '150px',
            padding: '1rem',
            borderRadius: '12px',
            border: 'none',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            fontSize: '1rem',
            opacity: loading ? 0.6 : 1
          }}
        >
          🖼️ Export NFT Metadata
        </button>

        <button
          onClick={() => exportCollection('csv')}
          disabled={loading}
          style={{
            flex: 1,
            minWidth: '150px',
            padding: '1rem',
            borderRadius: '12px',
            border: 'none',
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            fontSize: '1rem',
            opacity: loading ? 0.6 : 1
          }}
        >
          📊 Export CSV
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '1rem',
          borderRadius: '12px',
          background: message.startsWith('✅') 
            ? 'rgba(0, 255, 0, 0.1)' 
            : message.startsWith('❌') 
              ? 'rgba(255, 0, 0, 0.1)' 
              : 'rgba(255, 193, 7, 0.1)',
          border: `1px solid ${
            message.startsWith('✅') 
              ? 'rgba(0, 255, 0, 0.3)' 
              : message.startsWith('❌') 
                ? 'rgba(255, 0, 0, 0.3)' 
                : 'rgba(255, 193, 7, 0.3)'
          }`,
          color: 'white',
          textAlign: 'center'
        }}>
          {message}
        </div>
      )}

      {/* Info Box */}
      <div style={{
        marginTop: '2rem',
        padding: '1.5rem',
        background: 'rgba(100, 100, 255, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(100, 100, 255, 0.3)'
      }}>
        <h4 style={{ color: '#64b5f6', marginBottom: '0.75rem' }}>ℹ️ Export Formats</h4>
        <ul style={{ color: '#ccc', lineHeight: '2', marginLeft: '1.5rem', fontSize: '0.9rem' }}>
          <li><strong>JSON:</strong> Raw collection data with all mint details</li>
          <li><strong>NFT Metadata:</strong> OpenSea/marketplace-ready JSON format</li>
          <li><strong>CSV:</strong> Spreadsheet format for analysis</li>
        </ul>
      </div>
    </div>
  );
}

export default NadscriptionExport;

